import { CClient, showErrorAlert, staticFile } from '@common/utils'
import AutoSlideSwitcher from '@components/quizzes/arena/problems/auto-slide-switcher'
import PauseMenu from '@components/quizzes/arena/problems/pause-menu'
import Problem from '@components/quizzes/arena/problems/problem'
import ProblemBottomNavbar from '@components/quizzes/arena/problems/problem-bottom-navbar'
import ProblemNumber from '@components/quizzes/arena/problems/problem-number'
import ProblemStatusBar from '@components/quizzes/arena/problems/problem-status-bar'
import ResultBottomBar from '@components/quizzes/arena/problems/result-bottom-bar'
import Solution from '@components/quizzes/arena/problems/solution'
import {
  PROBLEM_STEP_COUNT,
  QUIZZES_SOUND_PATH,
  QUIZ_ARENA_STATE,
  SLIDE_DIRECTION,
} from '@lib/constants/quizzes'
import {
  QuizProblemType,
  QuizProblemResponseType,
  QuizSubmissionResponseType,
  SlideType,
  SlideDirectionType,
} from '@lib/types/quizzes'
import { useSpring, useTransition } from '@react-spring/core'
import { animated } from '@react-spring/web'
import * as Sentry from '@sentry/node'
import date from '@lib/utils/date.utils'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useBackgroundMusic from '@hooks/useBackgroundMusic'
import { getAPIErrorString, playSound } from '@lib/utils/common'

/*
States:
1. Attempting the question:
	- Timer is running
	- Next and back buttons not shown
	- Flashes the question number before showing the question
2. Showing solution
	- Timer is paused
	- Next and back buttons are shown and functional
	- Slide example: Q1(Correct), Q2(Wrong), Q3(Correct), Q4(Wrong, current question), Q4's Solution
3. Paused
	- Timer is not affected
	- Next and back buttons are hidden
*/

type Props = {
  quizId: number
  totalProblems: number
  currentProblemIndex: number
  quizTitle: string
  currentPoints: number
  setCurrentPoints: Dispatch<SetStateAction<number>>
  attemptedProblems: QuizProblemType[]
  setAttemptedProblems: Dispatch<SetStateAction<QuizProblemType[]>>
  setArenaState: Dispatch<SetStateAction<string>>
  quizSlug: string
  problemIds: number[]
  isLessonQuiz: boolean
}

const ProblemsView = ({
  quizId,
  currentProblemIndex: currentProblemIndexProp,
  quizTitle,
  totalProblems,
  currentPoints,
  setCurrentPoints,
  attemptedProblems,
  setAttemptedProblems,
  setArenaState,
  quizSlug,
  problemIds,
  isLessonQuiz,
}: Props) => {
  const { isBackgroundMusicOn, setIsBackgroundMusicOn } = useBackgroundMusic({
    playOnMount: true,
    pathToAudioFile: `${QUIZZES_SOUND_PATH}background-track.mp3`,
  })
  const [isPaused, setIsPaused] = useState(false)
  const [isBackButtonDisabled, setIsBackButtonDisabled] = useState(false)
  const [
    areProblemNavigationButtonsShown,
    setAreProblemNavigationButtonsShown,
  ] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentProblem, setCurrentProblem] = useState<QuizProblemType>(null)
  const [currentSolution, setCurrentSolution] = useState('')
  const [showProblemNumber, setShowProblemNumber] = useState(true)
  const [slideDirection, setSlideDirection] = useState<SlideDirectionType>(
    SLIDE_DIRECTION.previous
  )
  const [isValidating, setIsValidating] = useState(false)
  const [selectedChoiceIds, setSelectedChoiceIds] = useState<number[]>([])
  const [slides, setSlides] = useState<SlideType[]>([
    ...attemptedProblems.map((problem) => ({
      slideType: 'problem',
      problem,
    })),
  ])
  const [activeSlideIndex, setActiveSlideIndex] = useState(
    attemptedProblems.length
  )
  const [currentProblemIndex, setCurrentProblemIndex] = useState(
    currentProblemIndexProp
  )

  const correctAnswerSound = useMemo(
    () => new Audio(staticFile(`${QUIZZES_SOUND_PATH}correct.mp3`)),
    []
  )
  const wrongAnswerSound = useMemo(
    () => new Audio(staticFile(`${QUIZZES_SOUND_PATH}wrong.mp3`)),
    []
  )

  const fetchNextProblem = useCallback(
    async (problemId: number) => {
      if (isLoading) return
      setShowProblemNumber(true)
      setTimeout(() => setShowProblemNumber(false), 1500)
      setIsLoading(true)
      setAreProblemNavigationButtonsShown(false)

      try {
        const response = await CClient(
          `/api/v1/competitions/${quizSlug}/quiz-problems/${problemId}/`
        )
        if (!response.ok) throw new Error(response.statusText)

        const data: QuizProblemResponseType = await response.json()

        const newProblem: QuizProblemType = {
          ...data,
          submittedChoices: [],
          correctChoices: [],
        }

        setCurrentProblem(newProblem)
        setAttemptedProblems((prev) => [...prev, newProblem])
        setIsLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        showErrorAlert({
          text:
            'Something went wrong while fetching the problem. Please refresh and try again.',
        })
        setIsLoading(false)
      }
    },
    [isLoading]
  )

  const onSubmit = useCallback(
    async (selectedChoiceIds: number[]) => {
      if (isValidating) return
      setIsValidating(true)
      timerAnimationAPI.stop()

      try {
        const body = {
          problem: currentProblem.id,
          quiz: quizId,
          submittedChoices: selectedChoiceIds,
          validationKey: currentProblem.validationKey,
          showedTimestamp: currentProblem.showedTimestamp,
          sessionKey: currentProblem.sessionKey,
        }

        const apiEndpoint = `/api/v1/competitions/${quizSlug}/quiz-submissions/`
        const response = await CClient(apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          throw Error(await getAPIErrorString(response, apiEndpoint))
        }

        const data: QuizSubmissionResponseType = await response.json()

        const isCorrect = data.result !== 'Wrong Answer'

        playSound(isCorrect ? correctAnswerSound : wrongAnswerSound)
        const updatedCurrentProblem: QuizProblemType = {
          ...currentProblem,
          isAnswerValidated: true,
          submittedChoices: selectedChoiceIds,
          isCorrect,
          correctChoices: isCorrect
            ? selectedChoiceIds
            : data.solution.map((correctChoice) => correctChoice.id),
        }
        setCurrentProblem(updatedCurrentProblem)
        if (currentProblemIndex === 0) setIsBackButtonDisabled(true)
        setAreProblemNavigationButtonsShown(true)
        setCurrentPoints((prev) => prev + data.pointsObtained)
        setCurrentSolution(data.solutionExplaination)
        setIsValidating(false)
      } catch (error) {
        Sentry.captureException(error)
        showErrorAlert({
          text: 'Something went wrong. Please refresh and try again.',
        })
        setIsValidating(false)
      }
    },
    [isValidating, currentProblem?.id, correctAnswerSound, wrongAnswerSound]
  )

  const handlePreviousProblemClick = useCallback(() => {
    setSlideDirection(SLIDE_DIRECTION.previous)
    if (activeSlideIndex === 1) setIsBackButtonDisabled(true)
    setActiveSlideIndex((prev) => prev - 1)
  }, [activeSlideIndex])

  const handleNextProblemClick = useCallback(() => {
    setSlideDirection(SLIDE_DIRECTION.next)
    setIsBackButtonDisabled(false)
    if (
      activeSlideIndex === slides.length - 1 &&
      currentProblemIndex === totalProblems - 1
    ) {
      setArenaState(QUIZ_ARENA_STATE.COMPLETE)
      return
    }

    if (activeSlideIndex === slides.length - 1) {
      // need to fetch a new problem as next was clicked on the last slide
      fetchNextProblem(problemIds[currentProblemIndex + 1])
      setCurrentProblemIndex((prevState) => prevState + 1)
    }
    setActiveSlideIndex((prev) => prev + 1)
  }, [activeSlideIndex, currentProblemIndex, slides.length])

  useEffect(() => {
    fetchNextProblem(problemIds[currentProblemIndexProp])
  }, [])

  // Updating selected choices according to active slide
  useEffect(() => {
    setSelectedChoiceIds(
      slides[activeSlideIndex]?.problem?.submittedChoices || []
    )
  }, [activeSlideIndex])

  // Updates slides
  useEffect(() => {
    const newSlides: SlideType[] = [
      ...attemptedProblems.map((problem) => ({
        slideType: 'problem',
        problem,
      })),
    ]

    if (currentProblem?.isAnswerValidated && !currentProblem?.isCorrect) {
      newSlides.push({ slideType: 'solution' })
      // Whenver solution slide is pushed to the array, user is on the current problem slide which is the second last item
      // hence activeSlideIndex is set to `newSlideObj.length - 2`
      setActiveSlideIndex(newSlides.length - 2)
    } else setActiveSlideIndex(newSlides.length - 1)
    setSlides(newSlides)
  }, [
    attemptedProblems,
    currentProblem?.isAnswerValidated,
    currentProblem?.isCorrect,
  ])

  // Need to update the current problem in the attempted problem array so updated values can be shown
  useEffect(() => {
    // Early return if the last attempted problem is not the current problem
    // Can happen when current problem has not been added to attemptedProblems but updated in currentProblem state
    if (
      attemptedProblems[attemptedProblems.length - 1]?.id !==
        currentProblem?.id ||
      !currentProblem
    )
      return
    const currentAttemptedProblems = attemptedProblems
    currentAttemptedProblems.pop()
    setAttemptedProblems([...currentAttemptedProblems, currentProblem])
  }, [currentProblem])

  const [timerStyle, timerAnimationAPI] = useSpring(() => ({
    from: { width: '100%', backgroundColor: '#3DCE4E' },
  }))

  useEffect(() => {
    if (!currentProblem) return
    if (!isLoading && !showProblemNumber) {
      const timeLimit = date(currentProblem.timeLimit, 'HH:mm:ss')
      const time =
        timeLimit.second() +
        timeLimit.minute() * 60 +
        timeLimit.hour() * 60 * 60

      // Starting timer when problem has been fetched and problem number has disappeared
      timerAnimationAPI
        .update({
          to: { width: '0%', backgroundColor: '#EA2337' },
          config: {
            duration: time * 1000,
          },
        })
        .start()
    } else {
      // Reseting timer when loading OR showing problem number
      timerAnimationAPI.set({ width: '100%', backgroundColor: '#3DCE4E' })
    }
  }, [isLoading, showProblemNumber])

  const [problemTransition] = useTransition(activeSlideIndex, () => ({
    from: { delay: 0, opacity: 0, x: slideDirection * 100, scale: 0.9 },
    enter: { delay: 200, opacity: 1, x: 0, scale: 1 },
    leave: { delay: 0, opacity: 0, x: slideDirection * -100, scale: 0.9 },
    config: { duration: 200 },
  }))

  return (
    <div className="relative flex flex-col h-full overflow-hidden text-white">
      <animated.div
        style={{ ...timerStyle, height: '8px', borderRadius: '10px' }}
      />
      <ProblemStatusBar
        currentPoints={currentPoints}
        attemptedProblemCount={attemptedProblems.length}
        totalProblems={totalProblems}
      />
      <ProblemNumber
        problemNumber={currentProblemIndex + 1}
        showProblemNumber={!isPaused && (showProblemNumber || isLoading)}
      />
      <div className="relative w-full h-full">
        {problemTransition((styles, slideIndex) => (
          <animated.div
            style={{ ...styles, position: 'absolute' }}
            className="w-full h-full pb-5"
          >
            {slides[slideIndex]?.slideType === 'problem' && (
              <Problem
                problem={slides[slideIndex]?.problem}
                isHidden={showProblemNumber || isLoading || isPaused}
                selectedChoiceIds={selectedChoiceIds}
                setSelectedChoiceIds={setSelectedChoiceIds}
                isValidating={isValidating}
                onSubmit={onSubmit}
              />
            )}
            {slides[slideIndex]?.slideType === 'solution' && (
              <Solution solutionContent={currentSolution} isPaused={isPaused} />
            )}
          </animated.div>
        ))}
        <PauseMenu
          isPaused={isPaused}
          quizId={quizId}
          slug={quizSlug}
          points={currentPoints}
          quizTitle={quizTitle}
          remainingQuestions={
            totalProblems -
            attemptedProblems.length +
            (currentProblem?.isAnswerValidated ? 0 : 1)
          }
          setIsPaused={setIsPaused}
          totalProblems={totalProblems}
          isLessonQuiz={isLessonQuiz}
        />
      </div>
      <ProblemBottomNavbar
        areProblemNavigationButtonsShown={areProblemNavigationButtonsShown}
        handleNextProblemClick={handleNextProblemClick}
        handlePreviousProblemClick={handlePreviousProblemClick}
        isBackButtonDisabled={isBackButtonDisabled}
        isBackgroundMusicOn={isBackgroundMusicOn}
        isPaused={isPaused}
        setIsBackgroundMusicOn={setIsBackgroundMusicOn}
        setIsPaused={setIsPaused}
        onSubmit={() => onSubmit(selectedChoiceIds)}
        isSubmitButtonShown={
          currentProblem?.problemType === 'Checkbox' &&
          !currentProblem?.isAnswerValidated &&
          !showProblemNumber
        }
      />
      <ResultBottomBar
        isAnswerValidated={currentProblem?.isAnswerValidated}
        isCorrect={currentProblem?.isCorrect}
      />
      <AutoSlideSwitcher
        activeSlide={activeSlideIndex - currentProblemIndex}
        totalSteps={
          currentProblem?.isCorrect
            ? PROBLEM_STEP_COUNT.correct
            : PROBLEM_STEP_COUNT.wrong
        }
        moveToNextSlide={handleNextProblemClick}
        isAnswerValidated={currentProblem?.isAnswerValidated}
        showProblemNumber={showProblemNumber}
        isPaused={isPaused}
      />
    </div>
  )
}

export default ProblemsView
